import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { asApiError } from '@/services/apiError'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { ProjectionValue } from '@/components/px/ProjectionValue'
import { SectionLabel } from '@/components/px/SectionLabel'
import { myNav } from '@/components/px/nav'
import { selectActiveOrgId, selectActiveRoleKey, selectSessionPrincipalKey } from '@/features/auth/slices/sessionSlice'
import {
  createHours,
  deleteHours,
  fetchProjectHours,
  hoursErrorDismissed,
  submitHours,
  updateHours,
} from '@/features/hours/slices/hoursSlice'
import {
  selectHoursCreating,
  selectHoursError,
  selectHoursMutatingIds,
  selectOwnProjectEntries,
  selectProjectHoursLoading,
} from '@/features/hours/selectors'
import type { HoursEntry, LogHoursInput } from '@/features/hours/types'
import { formatDay, formatMoney, formatMinutes } from '@/lib/money'
import { fetchProjects, selectProjectsItems } from '@/features/projects/slices/projectsSlice'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * MY TIMESHEET (`/my/timesheet`, PX-W6.4 AC-1): the signed-in member's own
 * entries of a selected project — log, edit, delete, submit — on the real
 * hours endpoints.
 *
 * The list is the project's rows filtered to the session user (a
 * presentational filter, plan decision 5); the server enforces self-only
 * writes regardless. Status is the server's two-state machine (I-2): a
 * submitted row renders read-only with its actions disabled, `approved` is
 * never a thing here. The rate column is the ONE redactable field — it goes
 * through `ProjectionValue`, so an absent `rateSnapshot` (CLIENT side, or an
 * hour no card priced) renders the "not shared" placeholder, never zero.
 */

/** The log form's draft state; `day` defaults to today, ISO. */
interface LogFormDraft {
  day: string
  minutes: string
  billable: boolean
  note: string
}

/**
 * The user's LOCAL calendar day as `"YYYY-MM-DD"` — not `toISOString()`, which
 * is UTC and would push an evening entry onto tomorrow's day for anyone east
 * of Greenwich (the wire day is a human calendar day, I-4).
 */
const todayIso = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`
}

const emptyDraft = (): LogFormDraft => ({ day: todayIso(), minutes: '', billable: true, note: '' })

/** Stable empty list — never allocate per selector call (see hours/selectors). */
const NO_ENTRIES: HoursEntry[] = []

export default function TimesheetPage() {
  const { t } = useTranslation('commercial')
  const dispatch = useAppDispatch()
  const role = useAppSelector(selectActiveRoleKey)
  const orgId = useAppSelector(selectActiveOrgId)
  // The PRINCIPAL KEY, not the slug: `MongoHoursEntry.userId` is stamped in
  // canonical hex, so comparing it against `session.user.id` (the slug, e.g.
  // "user-admin") matches nothing and the caller's own rows never render.
  const userId = useAppSelector(selectSessionPrincipalKey)
  const projects = useAppSelector(selectProjectsItems)
  const loading = useAppSelector(selectProjectHoursLoading)
  const error = useAppSelector(selectHoursError)
  const creating = useAppSelector(selectHoursCreating)
  const mutatingIds = useAppSelector(selectHoursMutatingIds)

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [draft, setDraft] = useState<LogFormDraft>(emptyDraft)
  const [formError, setFormError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<LogFormDraft>(emptyDraft)

  // The picker's project list (W6.3's portfolio slice — consumed, not owned).
  useEffect(() => {
    if (orgId === null) return
    void dispatch(fetchProjects(orgId))
  }, [dispatch, orgId])

  // Default the selection to the first readable project — a pure projection
  // of the loaded list, computed during render instead of an Effect (no
  // extra render round-trip, no cascading setState-in-effect).
  const projectId = selectedProjectId ?? (projects.length > 0 ? projects[0]!.id : null)

  // Load the selected project's entries — once per (project, invalidation).
  useEffect(() => {
    if (projectId === null) return
    void dispatch(fetchProjectHours(projectId))
  }, [dispatch, projectId])

  const entries = useAppSelector((s) =>
    projectId === null ? NO_ENTRIES : selectOwnProjectEntries(s, projectId, userId)
  )

  const onRetryClicked = (): void => {
    // Dismiss FIRST — it re-arms the cache (the fetchCache Retry contract),
    // so a failed post-mutation refetch (whose loadedKey still matches) is
    // refetchable instead of silently skipped.
    dispatch(hoursErrorDismissed())
    if (projectId !== null) void dispatch(fetchProjectHours(projectId))
  }

  const onLogClicked = async (): Promise<void> => {
    if (projectId === null) return
    const minutes = Number(draft.minutes)
    if (draft.day === '' || draft.minutes === '' || !Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
      setFormError(t('commercial:timesheet.minutesInvalid'))
      return
    }
    const input: LogHoursInput = {
      day: draft.day,
      minutes,
      billable: draft.billable,
      note: draft.note.trim() === '' ? undefined : draft.note.trim()
    }
    setFormError(null)
    const result = await dispatch(createHours({ projectId, input }))
    // The draft SURVIVES a failed create (review minor e): only a row that
    // landed on the server clears the form — a refused POST must not eat
    // the user's typing.
    if (createHours.fulfilled.match(result)) setDraft(emptyDraft())
  }

  const onEditStarted = (entry: HoursEntry): void => {
    setEditingId(entry.id)
    setEditDraft({
      day: entry.day,
      minutes: String(entry.minutes),
      billable: entry.billable,
      note: entry.note ?? ''
    })
  }

  const onEditSaved = async (entry: HoursEntry): Promise<void> => {
    if (projectId === null) return
    const minutes = Number(editDraft.minutes)
    if (
      editDraft.day === '' ||
      editDraft.minutes === '' ||
      !Number.isInteger(minutes) ||
      minutes < 1 ||
      minutes > 1440
    ) {
      setFormError(t('commercial:timesheet.minutesInvalid'))
      return
    }
    setFormError(null)
    const result = await dispatch(
      updateHours({
        projectId,
        entryId: entry.id,
        input: {
          // Full replace — every mutable field echoed, status untouched (the
          // row is a draft; the server keeps it one).
          versionId: entry.versionId,
          workPackageRef: entry.workPackageRef,
          day: editDraft.day,
          minutes,
          billable: editDraft.billable,
          note: editDraft.note.trim() === '' ? undefined : editDraft.note.trim()
        }
      })
    )
    // The edit row stays open on a failed save (review minor e).
    if (updateHours.fulfilled.match(result)) {
      setEditingId(null)
      setEditDraft(emptyDraft())
    }
  }

  const onDeleteClicked = (entry: HoursEntry): void => {
    if (projectId === null) return
    if (!window.confirm(t('commercial:timesheet.delete.confirm'))) return
    void dispatch(deleteHours({ projectId, entryId: entry.id }))
  }

  const onSubmitClicked = (entry: HoursEntry): void => {
    if (projectId === null) return
    void dispatch(submitHours({ projectId, entryId: entry.id }))
  }

  const inputClass =
    'rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

  return (
    <AppShell
      moduleLabel={t('commercial:chrome.module')}
      breadcrumb={<span>{t('commercial:timesheet.title')}</span>}
      contextPill={t('commercial:chrome.myWork').toUpperCase()}
      nav={myNav(role)}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('commercial:timesheet.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('commercial:timesheet.lede')}</p>
      </header>

      {projects.length === 0 ? (
        <div className="mt-5">
          <EmptyPanel
            title={t('commercial:timesheet.noProjects.title')}
            body={t('commercial:timesheet.noProjects.body')}
          />
        </div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <label htmlFor="timesheet-project" className="text-sm font-medium text-foreground">
              {t('commercial:timesheet.project')}
            </label>
            <select
              id="timesheet-project"
              value={projectId ?? ''}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className={inputClass}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <section className="mt-4 rounded-lg border border-border bg-card px-4 py-3.5" aria-label={t('commercial:timesheet.log.title')}>
            <SectionLabel>{t('commercial:timesheet.log.title')}</SectionLabel>
            <div className="mt-2 grid gap-3 md:grid-cols-4">
              <label className="block text-sm">
                <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
                  {t('commercial:timesheet.log.day')}
                </span>
                <input
                  type="date"
                  value={draft.day}
                  onChange={(e) => setDraft({ ...draft, day: e.target.value })}
                  className={`${inputClass} w-full`}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
                  {t('commercial:timesheet.log.minutes')}
                </span>
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={draft.minutes}
                  onChange={(e) => setDraft({ ...draft, minutes: e.target.value })}
                  className={`${inputClass} w-full`}
                />
              </label>
              <label className="mt-6 flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={draft.billable}
                  onChange={(e) => setDraft({ ...draft, billable: e.target.checked })}
                  className="h-4 w-4"
                />
                {t('commercial:timesheet.log.billable')}
              </label>
              <label className="block text-sm md:col-span-2">
                <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
                  {t('commercial:timesheet.log.note')}
                </span>
                <textarea
                  rows={2}
                  maxLength={2000}
                  value={draft.note}
                  onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                  className={`${inputClass} w-full`}
                />
              </label>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => void onLogClicked()}
                disabled={creating || projectId === null}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {creating ? t('commercial:timesheet.log.logging') : t('commercial:timesheet.log.action')}
              </button>
              {formError !== null && (
                <p role="alert" className="text-sm text-blocked">
                  {formError}
                </p>
              )}
            </div>
          </section>

          <section className="mt-4" aria-label={t('commercial:timesheet.table.title')}>
            {loading ? (
              <LoadingPanel />
            ) : error !== null ? (
              error.status === 403 ? (
              // AC-6.2: the caller may not read this project's hours.
              <ForbiddenPanel requested="/{orgId}/projects/:projectId/hours" />
            ) : (
              <ErrorPanel error={asApiError(error)} onRetryClicked={onRetryClicked} />
            )
            ) : entries.length === 0 ? (
              <EmptyPanel
                title={t('commercial:timesheet.empty.title')}
                body={t('commercial:timesheet.empty.body')}
              />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="v-row border-b border-border">
                      <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('commercial:timesheet.table.day')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('commercial:timesheet.table.minutes')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('commercial:timesheet.table.billable')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('commercial:timesheet.table.note')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('commercial:timesheet.table.rate')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('commercial:timesheet.table.status')}
                      </th>
                      <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {t('commercial:timesheet.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => {
                      const mutating = mutatingIds.includes(entry.id)
                      const isDraft = entry.status === 'draft'
                      const editing = editingId === entry.id
                      return (
                        <tr key={entry.id} className="v-row border-b border-border last:border-b-0">
                          <td className="px-3 py-2.5 text-foreground">
                            {editing ? (
                              <input
                                type="date"
                                aria-label={t('commercial:timesheet.table.day')}
                                value={editDraft.day}
                                onChange={(e) => setEditDraft({ ...editDraft, day: e.target.value })}
                                className={`${inputClass} w-36`}
                              />
                            ) : (
                              formatDay(entry.day)
                            )}
                          </td>
                          <td className="v-figure px-3 py-2.5 text-right tabular-nums text-foreground">
                            {editing ? (
                              <input
                                type="number"
                                min={1}
                                max={1440}
                                aria-label={t('commercial:timesheet.table.minutes')}
                                value={editDraft.minutes}
                                onChange={(e) => setEditDraft({ ...editDraft, minutes: e.target.value })}
                                className={`${inputClass} w-24 text-right`}
                              />
                            ) : (
                              formatMinutes(entry.minutes)
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">
                            {editing ? (
                              <input
                                type="checkbox"
                                aria-label={t('commercial:timesheet.table.billable')}
                                checked={editDraft.billable}
                                onChange={(e) => setEditDraft({ ...editDraft, billable: e.target.checked })}
                                className="h-4 w-4"
                              />
                            ) : entry.billable ? (
                              t('commercial:common.yes')
                            ) : (
                              t('commercial:common.no')
                            )}
                          </td>
                          <td className="max-w-[22rem] px-3 py-2.5 text-muted-foreground">
                            {editing ? (
                              <textarea
                                rows={1}
                                maxLength={2000}
                                aria-label={t('commercial:timesheet.table.note')}
                                value={editDraft.note}
                                onChange={(e) => setEditDraft({ ...editDraft, note: e.target.value })}
                                className={`${inputClass} w-full`}
                              />
                            ) : (
                              entry.note ?? '—'
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <ProjectionValue
                              value={entry.rateSnapshot?.amountMinor}
                              format={(n) => formatMoney(n, entry.rateSnapshot?.currency)}
                            />
                          </td>
                          <td className="px-3 py-2.5 text-foreground">
                            {isDraft
                              ? t('commercial:common.statusDraft')
                              : t('commercial:common.statusSubmitted')}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right">
                            {/* I-2: only a draft row carries actions; a submitted
                                row is read-only for its author. */}
                            {!isDraft ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : editing ? (
                              <span className="inline-flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => void onEditSaved(entry)}
                                  disabled={mutating}
                                  className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                >
                                  {t('commercial:common.save')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingId(null)}
                                  className="rounded-md border border-input px-2 py-1 text-xs text-foreground hover:bg-muted"
                                >
                                  {t('commercial:common.cancel')}
                                </button>
                              </span>
                            ) : (
                              <span className="inline-flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => onEditStarted(entry)}
                                  disabled={mutating}
                                  className="rounded-md border border-input px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
                                >
                                  {t('commercial:common.edit')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onDeleteClicked(entry)}
                                  disabled={mutating}
                                  className="rounded-md border border-input px-2 py-1 text-xs text-foreground hover:bg-muted disabled:opacity-50"
                                >
                                  {t('commercial:common.delete')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onSubmitClicked(entry)}
                                  disabled={mutating}
                                  className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                >
                                  {t('commercial:timesheet.submit')}
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </AppShell>
  )
}
